/**
 * What the signed-in person may do in the operator area (F-69): `none` for everybody without a
 * platform role, `read` for operator support (`PlatformSupport`), `manage` for operator
 * administrators (`PlatformAdmin`). The client uses it only to choose the area and to show or hide
 * functions; the server checks the roles of every request on its own.
 */
export type PlatformAccess = "none" | "read" | "manage";

/** The access remembered on this device for one person. */
export interface RememberedPlatformAccess {
  /** Subject (`sub`) of the person. */
  readonly subject: string;
  /** The access the server named last. */
  readonly access: PlatformAccess;
}

/**
 * Remembers on this device whether the last signed-in person is operator staff, so an app start
 * without connection or before the server answered opens the right area at once: operator staff
 * never see the clock, and nothing of the clock is synchronized for them (F-69). It holds the subject
 * and the access only, no customer data. A failing storage forgets it; the server's answer decides
 * again on the next start.
 */
export interface PlatformAccessMarker {
  /**
   * Returns the access remembered for a person.
   *
   * @param subject - Subject of the signed-in person.
   * @returns The access, or `null` if nothing is remembered for this person.
   */
  read(subject: string): PlatformAccess | null;

  /**
   * Remembers the access of a person and forgets that of any other person.
   *
   * @param subject - Subject of the signed-in person.
   * @param access - The access the server named.
   */
  remember(subject: string, access: PlatformAccess): void;

  /** Forgets the remembered access, for example on sign-out (spec 11.4). */
  forget(): void;
}

/** Key of the marker in local storage. */
export const platformAccessKey = "zeitavo.platformAccess";

/**
 * Checks whether a value is a platform access.
 *
 * @param value - The value.
 * @returns `true` for a valid access.
 */
function isPlatformAccess(value: unknown): value is PlatformAccess {
  const valid = value === "none" || value === "read" || value === "manage";
  return valid;
}

/**
 * Reads a remembered access from its stored text.
 *
 * @param text - The stored text, or `null`.
 * @returns The remembered access, or `null` for nothing or an unreadable value.
 */
export function parseRememberedAccess(text: string | null): RememberedPlatformAccess | null {
  let remembered: RememberedPlatformAccess | null = null;

  try {
    const value: unknown = text === null ? null : JSON.parse(text);

    if (
      typeof value === "object" &&
      value !== null &&
      "subject" in value &&
      "access" in value &&
      typeof value.subject === "string" &&
      isPlatformAccess(value.access)
    ) {
      remembered = { subject: value.subject, access: value.access };
    }
  } catch {
    remembered = null;
  }

  return remembered;
}

/**
 * Creates the marker on top of a storage.
 *
 * @param storage - Local storage, or `null` where it is blocked.
 * @returns The marker.
 */
export function storagePlatformAccessMarker(storage: Storage | null): PlatformAccessMarker {
  const marker: PlatformAccessMarker = {
    read: (subject) => {
      let access: PlatformAccess | null;

      try {
        const remembered = parseRememberedAccess(storage?.getItem(platformAccessKey) ?? null);
        access = remembered?.subject === subject ? remembered.access : null;
      } catch {
        access = null;
      }

      return access;
    },
    remember: (subject, access) => {
      try {
        storage?.setItem(platformAccessKey, JSON.stringify({ subject, access }));
      } catch {
        // Without storage the next start asks the server again before it opens an area.
      }
    },
    forget: () => {
      try {
        storage?.removeItem(platformAccessKey);
      } catch {
        // Blocked storage held nothing.
      }
    },
  };

  return marker;
}

/**
 * Creates a marker that remembers in memory only (tests).
 *
 * @returns The marker.
 */
export function memoryPlatformAccessMarker(): PlatformAccessMarker {
  let remembered: RememberedPlatformAccess | null = null;
  const marker: PlatformAccessMarker = {
    read: (subject) => {
      const access = remembered?.subject === subject ? remembered.access : null;
      return access;
    },
    remember: (subject, access) => {
      remembered = { subject, access };
    },
    forget: () => {
      remembered = null;
    },
  };

  return marker;
}

/**
 * Derives the access from the platform roles of `GET /platform/v1/me`. Unknown roles grant nothing.
 *
 * @param roles - The roles.
 * @returns The access.
 */
export function platformAccessOf(roles: readonly string[]): PlatformAccess {
  const access: PlatformAccess = roles.includes("PlatformAdmin")
    ? "manage"
    : roles.includes("PlatformSupport")
      ? "read"
      : "none";

  return access;
}

/**
 * What the gate makes of the platform access (F-69, F-84): an access, `loading` while the first
 * answer is awaited, or `unavailable` when the last request failed (network, server) or cannot run
 * (offline) and the device neither remembers the access of the person nor holds data of them from the
 * application of the tenants.
 */
export type PlatformAccessDecision = PlatformAccess | "loading" | "unavailable";

/** What the platform roles query says at the moment. */
export interface PlatformRolesState {
  /** The roles from the server, or `undefined` while there is no answer. */
  readonly roles: readonly string[] | undefined;
  /**
   * Whether the last settled request failed or the request waits for a connection; a request that
   * is running does not change it (F-84).
   */
  readonly failed: boolean;
  /**
   * Whether the device holds data of the person from the application of the tenants (roles of a
   * settled `GET /me`, unsent entries; `hasTenantDataOnDevice`), or `null` while it is not known.
   */
  readonly tenantDataOnDevice: boolean | null;
}

/**
 * Decides the access of the signed-in person: the answer of the server; without one the access
 * remembered on this device for the same person; without either, after a failed request or offline,
 * the application of the tenants for a person whose tenant data the device holds, else
 * `unavailable`; while the first request runs, `loading`. A failure alone no longer counts as „no
 * platform role“ (F-84, as start screens decide on settled answers only, ADR-0206), so operator staff
 * never see the clock because the server was not reached: an account of operator staff only never has
 * tenant data on the device. Employees keep the clock offline through the remembered access or their
 * tenant data.
 *
 * @param state - The state of the query.
 * @param remembered - The access remembered for this person, or `null`.
 * @returns The decision.
 */
export function decidePlatformAccess(
  state: PlatformRolesState,
  remembered: PlatformAccess | null,
): PlatformAccessDecision {
  const withoutAnswer: PlatformAccessDecision = !state.failed
    ? "loading"
    : state.tenantDataOnDevice === null
      ? "loading"
      : state.tenantDataOnDevice
        ? "none"
        : "unavailable";
  const decided: PlatformAccessDecision =
    state.roles !== undefined ? platformAccessOf(state.roles) : (remembered ?? withoutAnswer);

  return decided;
}
